import React, { Fragment, useState } from 'react';
import { Table } from 'reactstrap';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import { FormattedMessage, injectIntl } from 'react-intl';
import { orderBy } from 'lodash';
import { Button, NavLink, Row, Col, Modal, ModalBody, ModalHeader } from 'reactstrap';
import StickyTable from '../../../../hoc/StickyTable';
import WithHover from '../../../../hoc/WithHover';
import Icon from '../../../../components/Icon/Icon';
import { FMessage, iFMessage } from '../../../../hoc/IntlComponents';
import {
  isString,
  getToFixedParams,
  _toFixed,
  getStringOrSingleArrayValue,
} from '../../../../utils/utils';
import { STATIC_PATH } from '../../../../config';
import StatisticsTableField from './StatisticsTableField';
import StatisticsConversionsRow from './StatisticsConversionsRow';
import { LocalStorage } from '../../../../utils/utils';
import Card from '../../../../components/Card/Card';
import StatisticsConversionsTableSettings from './StatisticsConversionsTableSettings';

const fieldConfigRaw = [
  {
    id: 126,
    title: 'money',
    is_sortable: true,
    type: 'money',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 202,
    title: 'lead_data',
    is_sortable: true,
    type: 'date',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 203,
    title: 'offer',
    is_sortable: true,
    type: 'offer_pixel',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 205,
    title: 'status',
    is_sortable: true,
    type: 'status_info',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 2040,
    title: 'geo',
    is_sortable: true,
    type: 'geo_ip_referrer_info',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 2041,
    title: 'ip',
    is_sortable: true,
    type: 'geo_ip_referrer_info',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 2051,
    title: 'comment',
    is_sortable: true,
    type: 'status_info',
    inverse: false,
    label: '',
    isRendering: true,
  },
  {
    id: 0,
    title: 'action',
    is_sortable: false,
    type: 'action',
    inverse: false,
    label: '',
    isRendering: true,
  },
];

const getCountryImg = id => <img src={`${STATIC_PATH}/flags/4x3/${id}.svg`} alt={id} />;

const StatisticsConversionsTable = ({
  stats,
  renderEmptyData,
  splitStats,
  page,
  config,
  currency,
  dict,
  offers,
  flows,
  filters,
  isMember,
  countries,
  intl,
  currencies,
}) => {
  const [sort, setSort] = useState({
    field: 202,
    order: -1,
    inverse: false,
  });

  const getFormattedValue = (value, type, statId) => {
    const tz = filters.timeZone;
    const { numFormat, strFormat } = getToFixedParams(config, statId);
    switch (type) {
      case 'int':
        return numeral(value).format('0,0');

      case 'float':
        return <span className="sum">{numeral(_toFixed(value, numFormat)).format(strFormat)}</span>;
      case 'money':
        <span className="sum">
          {value % 1 === 0 ? value : numeral(_toFixed(value, numFormat)).format(strFormat)}
        </span>

      case 'string':
        return value.toString();

      case 'stream_id':
        let stream;
        if (dict['flows'] !== undefined) {
          stream = dict['flows'].find(({ id }) => id === value);
        }
        if (!stream && flows) {
          stream = flows.find(({ id }) => id === value);
        }
        return stream && isString(stream.title) ? (
          !isMember ? (
            <Link to={`/tools/flows/${value}`}>{stream.title}</Link>
          ) : (
            stream.title
          )
        ) : (
          value
        );

      case 'date':
        return (
          <Fragment>
            <Moment unix format="DD.MM.YYYY" tz={tz}>
              {value}
            </Moment>{' '}
            <Moment unix format="HH:mm" tz={tz} className="gray">
              {value}
            </Moment>
          </Fragment>
        );
      case 'subacc':
        return value ? (
          <Fragment>
            <span id={value} title={value}>
              {value && value.length > 40 ? value.substr(0, 5) : value}
              {value && value.length > 40 ? '...' + value.substr(value.length - 5) : ''}
            </span>
          </Fragment>
        ) : null;

      case 'offer_pixel':
        let offer, pixel;

        if (Array.isArray(value)) {
          const [offerId, pixelId] = value;

          if (dict['offers'] !== undefined) {
            offer = dict['offers'].find(({ id }) => id === offerId);
          }
          if (!offer && offers) {
            offer = offers.find(({ id }) => id === offerId);
          }
          if (dict['pixels'] !== undefined) {
            pixel = dict['pixels'].find(({ id }) => id === pixelId);
          }
          if (!pixel && offers && offers.pixels) {
            pixel = offers.pixels.find(({ id }) => id === pixelId);
          }

          return offer && isString(offer.title) ? (
            !isMember ? (
              <Link to={`/offers/${offer.id}`} target="_blank">
                {offer.title}
              </Link>
            ) : (
              offer.title
            )
          ) : (
            offerId
          );
        }

        return value;

      case 'geo_ip_referrer_info':
        let country;

        if (Array.isArray(value)) {
          const [countryId, ip] = value;
          country = countries.find(country => country.id === countryId);
          const countryString = country ? (
            <span className="conversions-table-geo">
              {getCountryImg(country.id.toLowerCase())}
              <span>{country.title}</span>
            </span>
          ) : (
            'N/A'
          );

          return [countryString, ip];
        }

        return value;

      case 'status_info': {
        if (Array.isArray(value)) {
          const [statusId, , , extraParams] = value;
          switch (statusId) {
            case 0:
              return (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <Icon type="wait" />
                </span>
              );

            case 1:
              return (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <Icon type="circle-arrow-right" />
                </span>
              );

            case 2:
              return extraParams.isTrash ? (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <Icon type="denied" />
                </span>
              ) : (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <Icon type="cross-rounded" />
                </span>
              );
            case 3:
              return (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <Icon type="check-rounded" />
                </span>
              );

            default:
              return (
                <span
                  title={iFMessage({ intl, id: `statistics.table.conversions.status-${statusId}` })}
                >
                  <FMessage id={`statistics.table.conversions.status-${statusId}`} />
                </span>
              );
          }
        }

        return value;
      }

      default:
        return value;
    }
  };

  const normalizeData = (stat, key) => {
    const data = {};

    data['commonFields'] = config.map(({ id, type }) => {
      if (stat[id]) {
        return getFormattedValue(stat[id][currency], type, id, key);
      }
    });

    const statusField = config.find(({ type }) => type === 'status_info');
    
    data['info'] = statusField ? getOrderinfo(stat[statusField.id][currency]) : {};

    const subaccField = config.find(({ type }) => type === 'subacc');

    data['subacc'] = subaccField
      ? getFormattedValue(stat[subaccField.id][currency], subaccField.type)
      : {};

    return data;
  };

  const formatComment = comment =>
    comment.split('\n').map((com, key) => <div key={key}>{com}</div>);

  const getOrderinfo = value => {
    const statuses = [{ id: 0 }, { id: 1 }, { id: 2 }, { id: 3 }];
    if (Array.isArray(value)) {
      const [statusId, , popupInfo, extraParams] = value;
      const status = statuses.find(({ id }) => id === parseInt(statusId, 10));
      const { p: phone, c: comm } = popupInfo;
      const comment = comm ? formatComment(comm) : null;
      const isTrash = extraParams ? extraParams.isTrash : false;
      return {
        comment,
        phone,
        status,
        isTrash,
      };
    }
  };

  const getOrderedStats = () => {
    const direction = sort.inverse ? sort.order * -1 : sort.order * 1;

    if (stats.length > 0) {
      if (sort.field === 2041) {
        return orderBy(stats, `[204][${currency}][1]`, direction > 0 ? 'asc' : 'desc');
      }

      if (sort.field === 2051) {
        return orderBy(stats, `[205][${currency}][2][c]`, direction > 0 ? 'asc' : 'desc');
      }

      return orderBy(stats, `${sort.field}[${currency}]`, direction > 0 ? 'asc' : 'desc');
    }

    return stats;
  };

  const mapStats = () => {
    const stats = splitStats(getOrderedStats());
    return stats[page].map((stat, key) => {
      return normalizeData(stat, key);
    });
  };

  const renderStats = () => {
    const stats = mapStats();

    return stats.map((data, index) => (
      <StatisticsConversionsRow
        {...data}
        config={config}
        index={index}
        currency={currency}
        currencies={currencies}
        fieldConfig={fieldConfig}
        key={`conversion-table-row-${index}`}
      />
    ));
  };

  const handleSort = (field, inverse, e) => {
    e.preventDefault();

    let order = field === sort.field ? sort.order * -1 : -1;
    setSort({
      field,
      order,
      inverse,
    });
  };

  const renderTableFields = () => {
    return fieldConfig.map(field => (
      <StatisticsTableField
        onSort={handleSort}
        {...field}
        title={iFMessage({ intl, id: `statistics.table.conversions.title.${field.title}` })}
        key={field.id}
        sorted={sort.field === field.id}
        order={sort.order}
        isInConversionsTable={true}
        isRendering={field.isRendering}
      />
    ));
  };

  const saveSettings = settings => {
    const updatedSetting = settings.map(field => {
      const currentField = fieldConfig.find(config => config.id === field.id);
      currentField.isRendering = field.checked;
      return currentField;
    });
    LocalStorage.set('StatisticsConversionsTableSettings', updatedSetting);
    setFieldConfig(updatedSetting);
  };

  const loadSettings = () => {
    const settings = LocalStorage.get('StatisticsConversionsTableSettings');
    return settings ? settings : fieldConfigRaw;
  };

  const [settingsModalOpen, toggleSettingsModal] = useState(false);

  const [fieldConfig, setFieldConfig] = useState(loadSettings());

  return (
    <Fragment>
      <Card.Header className="no-padding text-right">
        <Col>
          <Button
            color="primary"
            className="table-settings-btn"
            size="sm"
            onClick={() => toggleSettingsModal(!settingsModalOpen)}
          >
            <Icon type="kma-panel" tag="i" />
          </Button>
        </Col>
      </Card.Header>
      <StickyTable>
        <Table className="statistics-table conversion-table sticky-table inverse" responsive>
          <thead>
            <tr className="table-fields">{renderTableFields()}</tr>
          </thead>
          <tbody>{stats.length > 0 ? renderStats() : renderEmptyData()}</tbody>
        </Table>
      </StickyTable>

      <Modal
        className="modal-logs-settings"
        isOpen={settingsModalOpen}
        toggle={() => toggleSettingsModal(!settingsModalOpen)}
        size="lg"
      >
        <ModalHeader>
          <FormattedMessage id="postback.logs.settings" defaultMessage="Customize fields" />
          <Button
            onClick={() => toggleSettingsModal(!settingsModalOpen)}
            close
            style={{ float: 'right' }}
          />
        </ModalHeader>
        <ModalBody>
          <StatisticsConversionsTableSettings
            toggleSettingsModal={toggleSettingsModal}
            saveSettings={saveSettings}
            fieldConfig={fieldConfig}
          />
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default injectIntl(StatisticsConversionsTable);
